import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { HD_LOCKS_KEYS_INTRO_SECTION_KEYS } from './hd-locks-keys-metadata';
import { HdGlobalCyclesTableComponent } from './hd-global-cycles-table.component';
import { openHdGlobalCyclesTablePanel } from './hd-global-cycles-table-panel.component';
import { HdLocksKeysPanelLinkComponent } from './hd-locks-keys-panel-link.component';
import {
  HD_LOCKS_KEYS_INTRO_SECTION_LINKS,
  HD_LOCKS_KEYS_READING_PANELS,
  HdLocksKeysReadingPanelId,
} from './hd-locks-keys-reading-panels';
import { HdLocksKeysSectionComponent } from './hd-locks-keys-section.component';

export const HD_LOCKS_KEYS_INTRO_PANEL_KEY = 'locks-keys-intro';

interface LocksKeysIntroSectionVm {
  readonly key: string;
  /** Optionele doorlink naar een vervolgpaneel onderaan de sectie. */
  readonly linkPanel: HdLocksKeysReadingPanelId | undefined;
}

/**
 * Uitlegpaneel van de Locks & Keys-modus: de mechanica van de precessie-klok,
 * de locks (G-centrum), de 8-puntige key-ster en de transitie-indicatoren,
 * de introductiecontent uit de `human-design/locks-keys`-scope. De secties
 * rendert de gedeelde `hd-locks-keys-section` (zelfde model als de
 * vervolgpanelen); dit paneel voegt de Global Cycles-preview en de
 * doorklikkaarten toe.
 */
@Component({
  selector: 'hd-locks-keys-intro-panel',
  imports: [SharedModule, HdGlobalCyclesTableComponent, HdLocksKeysPanelLinkComponent, HdLocksKeysSectionComponent],
  templateUrl: './hd-locks-keys-intro-panel.component.html',
  styleUrl: './hd-locks-keys-intro-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdLocksKeysIntroPanelComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  /**
   * Verkleinde, klikbare preview van de Global Cycles-tabel (vóór de
   * tabel-uitlegsectie): de echte tabelcomponent, via transform geschaald naar
   * de paneelbreedte — herkenbaar als "image", altijd scherp en zonder
   * dubbele databron. De schaal volgt de containerbreedte (ResizeObserver);
   * de tabel heeft een vaste ontwerpbreedte van 960px.
   */
  private readonly previewHost = viewChild<ElementRef<HTMLElement>>('gcPreviewHost');
  private readonly previewInner = viewChild<ElementRef<HTMLElement>>('gcPreviewInner');
  protected readonly previewScale = signal(0.34);
  protected readonly previewHeight = signal(300);

  constructor() {
    afterNextRender(() => {
      const host = this.previewHost()?.nativeElement;
      const inner = this.previewInner()?.nativeElement;
      if (host == null || inner == null) {
        return;
      }
      const measure = () => {
        // scrollWidth/-Height zijn de natuurlijke (ongeschaalde) maten; een
        // transform beïnvloedt de layoutmaat niet.
        const naturalWidth = inner.scrollWidth;
        const naturalHeight = inner.scrollHeight;
        if (naturalWidth === 0 || host.clientWidth === 0) {
          return;
        }
        const scale = host.clientWidth / naturalWidth;
        this.previewScale.set(scale);
        this.previewHeight.set(Math.ceil(naturalHeight * scale));
      };
      measure();
      const observer = new ResizeObserver(() => measure());
      observer.observe(host);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  protected openGlobalCyclesTable(): void {
    openHdGlobalCyclesTablePanel(this.floatingPanels);
  }

  protected previewLabel(): string {
    return this.gateInfo.locksKeysTextIn('globalCycles.previewHint', this.lang());
  }

  /** Doorklikkaarten onderaan (leesflow-einde): de vervolgpanelen, "Mens na 2027" voorop. */
  protected readonly readingPanelIds: readonly HdLocksKeysReadingPanelId[] =
    HD_LOCKS_KEYS_READING_PANELS.map(panel => panel.id);

  protected readonly readOnTitle = computed(() =>
    this.gateInfo.locksKeysTextIn('intro.readOnTitle', this.lang()),
  );

  protected readonly title = computed(() =>
    this.gateInfo.locksKeysTextIn('intro.title', this.lang()),
  );

  protected readonly sections: readonly LocksKeysIntroSectionVm[] = HD_LOCKS_KEYS_INTRO_SECTION_KEYS.map(key => ({
    key,
    linkPanel: HD_LOCKS_KEYS_INTRO_SECTION_LINKS[key],
  }));
}
